import { type ReactNode, useEffect } from 'react';
import { ChatProvider } from './ChatProvider';
import { useChatState } from './chatState';
import { ChatSidebar } from './ChatSidebar';
import type { ChatContext } from './types';

type Props = {
  context: ChatContext;
  children: ReactNode;
};

export function ChatLayoutWrapper({ context, children }: Props) {
  return (
    <ChatProvider context={context}>
      <ChatLayoutInner>{children}</ChatLayoutInner>
    </ChatProvider>
  );
}

function ChatLayoutInner({ children }: { children: ReactNode }) {
  const { isOpen, open, setInitialMessage, context } = useChatState();

  useEffect(() => {
    const explicitWhatsappNodes = Array.from(
      document.querySelectorAll('[data-floating-action="whatsapp-support"]')
    ) as HTMLElement[];
    const fallbackWhatsappNodes = Array.from(document.querySelectorAll('img[alt="WhatsApp"]'))
      .map((image) => image.closest("button, a") as HTMLElement | null)
      .filter((node): node is HTMLElement => Boolean(node));
    const whatsappNodes = Array.from(new Set([...explicitWhatsappNodes, ...fallbackWhatsappNodes]));

    whatsappNodes.forEach((node) => {
      if (isOpen) {
        node.dataset.financePreviousDisplay = node.style.display;
        node.style.display = "none";
      } else if (node.dataset.financePreviousDisplay !== undefined) {
        node.style.display = node.dataset.financePreviousDisplay;
        delete node.dataset.financePreviousDisplay;
      }
    });

    return () => {
      whatsappNodes.forEach((node) => {
        if (node.dataset.financePreviousDisplay !== undefined) {
          node.style.display = node.dataset.financePreviousDisplay;
          delete node.dataset.financePreviousDisplay;
        }
      });
    };
  }, [isOpen]);

  useEffect(() => {
    const handleOpenAi = () => {
      setInitialMessage(null);
      open();
    };

    window.addEventListener("finance:open-ai-chat", handleOpenAi);
    return () => window.removeEventListener("finance:open-ai-chat", handleOpenAi);
  }, [open, setInitialMessage]);

  const reserveFloatingRail = !isOpen && context.surface !== "reports";
  const wrapperPadding = isOpen ? "lg:pr-[430px]" : reserveFloatingRail ? "pr-12" : "";

  return (
    <div className={`min-h-screen w-full transition-[padding] duration-200 ${wrapperPadding}`}>
      {children}
      <ChatSidebar />
    </div>
  );
}
